import {
  ConfirmStepUpOutcome,
  ContactVerificationChannel,
  StartStepUpOutcome,
} from '../../_data/types'

/**
 * Names the channel the way a renter would say it.
 *
 * @param {ContactVerificationChannel | null} channel - the channel the code went to
 * @returns {string} the word to use in a sentence
 */
const describeChannel = (
  channel: ContactVerificationChannel | null,
): string => {
  if (channel === 'phone') {
    return 'text message'
  }

  return 'email'
}

/**
 * Turns the result of asking for a code into something worth reading.
 *
 * @param {StartStepUpOutcome} outcome - what the server did
 * @param {ContactVerificationChannel | null} channel - the channel the code went to
 * @param {string} maskedDestination - where it went, masked
 * @param {number} retryAfterSeconds - how long until another send is allowed
 * @returns {string} the message to show
 */
const resolveStartMessage = (
  outcome: StartStepUpOutcome,
  channel: ContactVerificationChannel | null,
  maskedDestination: string,
  retryAfterSeconds: number,
): string => {
  if (outcome === 'sent') {
    return `We sent a code by ${describeChannel(channel)} to ${maskedDestination}.`
  }

  if (outcome === 'cooldown') {
    return `A code has already gone out. You can ask for another in ${retryAfterSeconds} seconds.`
  }

  if (outcome === 'send-limit') {
    return 'That is too many codes for now. Please wait a few minutes and try again.'
  }

  if (outcome === 'no-destination') {
    return 'We do not have a phone number or email address on file to send a code to. Add one to your profile first.'
  }

  return 'We could not send the code just now. Please try again in a moment.'
}

/**
 * Turns the result of checking a code into something worth reading.
 *
 * @param {ConfirmStepUpOutcome} outcome - what the server decided
 * @param {number} attemptsRemaining - how many tries are left
 * @returns {string} the message to show
 */
const resolveConfirmMessage = (
  outcome: ConfirmStepUpOutcome,
  attemptsRemaining: number,
): string => {
  if (outcome === 'invalid-code') {
    if (attemptsRemaining <= 0) {
      return 'That code is not right, and you are out of tries. Ask for a new one.'
    }

    return `That code is not right. ${attemptsRemaining} ${attemptsRemaining === 1 ? 'try' : 'tries'} left.`
  }

  if (outcome === 'expired') {
    return 'That code has expired. Ask for a new one.'
  }

  if (outcome === 'too-many-attempts') {
    return 'Too many tries. Ask for a new code to start again.'
  }

  if (outcome === 'not-found') {
    return 'We could not find that request. Ask for a new code.'
  }

  return 'We could not check that code just now. Please try again.'
}

export { describeChannel, resolveStartMessage, resolveConfirmMessage }
